import { TFunction } from 'i18next'
import { NavigateFunction } from 'react-router-dom'
import { UseFormSetValue } from 'react-hook-form'
import EditIcons from '@/icons/EditIcons'
import UserGroupIcons from '@/icons/UserGroupIcons'
import LoadIcons from '@/icons/LoadIcons'
import TimeIcons from '@/icons/TimeIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { DataTableAction } from '@/components/ui/data-table-customized'
import { FilterItemConfig } from '@/components/ui/data-table-customized'
import { generatePathDatacentral, paths } from '@/utils/path'
import {
    EMPLOYEE_CONTRACT_TYPE,
    EMPLOYEE_GENDER,
    EMPLOYEE_SENIORITY_LEVEL,
} from '../constants'
import { Employee } from '../types/response'

// ============================================================================
// Filter Items Config
// ============================================================================

interface EmployeeFilters {
    employee_contract_type: string
    employee_seniority_level: string
    position_is_executive: string
    employee_gender: string
    employee_status: number[]
    birthday_range: string[]
    birthday_range_to: string[]
    start_date_range: string[]
    start_date_range_to: string[]
    end_date_range: string[]
    end_date_range_to: string[]
    department_id: number
    position_id: number
}

interface FilterItemsParams {
    t: TFunction
    setValue: UseFormSetValue<EmployeeFilters>
    setFilters: (filters: any) => void
    /** Show end_date_range (resignation date) filter — enabled only on resigned tab */
    showEndDateRange?: boolean
}

export const getFilterItems = ({
    t,
    setValue,
    setFilters,
    showEndDateRange = false,
}: FilterItemsParams): FilterItemConfig[] => {
    const items: FilterItemConfig[] = [
        {
            name: 'employee_contract_type',
            type: 'select',
            label: t('employee_contract_type'),
            placeholder: t('employee_contract_type_placeholder'),
            options: EMPLOYEE_CONTRACT_TYPE(t),
        },
        {
            name: 'employee_seniority_level',
            type: 'select',
            label: t('employee_seniority_level'),
            placeholder: t('employee_seniority_level_placeholder'),
            options: EMPLOYEE_SENIORITY_LEVEL,
        },
        {
            name: 'position_is_executive',
            type: 'select',
            label: t('position_is_executive', { ns: 'employee' }),
            placeholder: t('position_is_executive', { ns: 'employee' }),
            options: [
                { label: t('yes'), value: 'true' },
                { label: t('no'), value: 'false' },
            ],
        },
        {
            name: 'employee_gender',
            type: 'select',
            label: t('gender'),
            placeholder: t('gender_placeholder'),
            options: EMPLOYEE_GENDER(t),
        },
        {
            name: 'start_date_range',
            type: 'daterangepicker',
            names: ['start_date_range', 'start_date_range_to'],
            label: t('employee:start_date_range'),
            showPresets: true,
        },
    ]

    if (showEndDateRange) {
        items.push({
            name: 'end_date_range',
            type: 'daterangepicker',
            names: ['end_date_range', 'end_date_range_to'],
            label: t('employee:end_date_range'),
            showPresets: true,
        })
    }

    items.push({
        name: 'department_id',
        type: 'department',
        onChange: (val: number) => {
            setValue('position_id', 0)
            setFilters({ department_id: val, position_id: 0 })
        },
    })

    return items
}

// Position filter needs to be added separately since it depends on department_id
export const getPositionFilterItem = (
    departmentId: number
): FilterItemConfig => ({
    name: 'position_id',
    type: 'position',
    departmentId,
})

// ============================================================================
// Table Actions Config
// ============================================================================

interface ActionsParams {
    t: TFunction
    navigate: NavigateFunction
    setConfirmation: (config: any) => void
    setModal: (path: string, props: any) => void
    resend: (data: { employee_id: number }) => Promise<any>
    updateProbationDate: (data: {
        employee_id: number
        probation_date: string
    }) => Promise<any>
    mutateAsync: (data: {
        employee_id: number
        off_date: string
    }) => Promise<any>
}

export const getActions = ({
    t,
    navigate,
    setConfirmation,
    setModal,
    resend,
    updateProbationDate,
    mutateAsync,
}: ActionsParams): DataTableAction<Employee>[] => [
    {
        key: 'edit',
        label: t('edit'),
        icon: <EditIcons className="w-4 h-4" />,
        onClick: (row) =>
            navigate(
                generatePathDatacentral(paths.employeeUpdate, {
                    id: row.employee_id,
                })
            ),
    },
    {
        key: 'edit-advanced',
        label: t('edit-advanced'),
        icon: <UserGroupIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) =>
            navigate(
                generatePathDatacentral(paths.employeeUpdateAdvanced, {
                    id: row.employee_id,
                })
            ),
    },
    {
        key: 'resend-confirmation',
        label: t('resend-confirmation'),
        icon: <LoadIcons className="w-4 h-4" />,
        variant: 'warning',
        onClick: (row) =>
            setConfirmation({
                open: true,
                title: t('confirm'),
                description: t('resend-confirmation-warning'),
                onConfirm: () => resend({ employee_id: row.employee_id }),
            }),
        hidden: (row) => row.employee_confirmed || row.employee_status !== 1,
    },
    {
        key: 'update-probation-date',
        label: t('update_probation_date'),
        icon: <TimeIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) =>
            setModal('EmployeeModals/ProbationDateModal/index', {
                employee_id: row.employee_id,
                employee_name: row.employee_name,
                onConfirm: (data: {
                    employee_id: number
                    probation_date: string
                }) => updateProbationDate(data),
            }),
        hidden: (row) => row.employee_status !== 1,
    },
    {
        key: 'disable',
        label: t('employee_disabled'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        onClick: (row) =>
            setModal('EmployeeModals/Disabled/index', {
                employee_id: row.employee_id,
                employee_name: row.employee_name,
                onConfirm: (data: { employee_id: number; off_date: string }) =>
                    mutateAsync(data),
            }),
        hidden: (row) => row.employee_status !== 1,
    },
]
